import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ImageOff, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ImageLightboxProps {
  /** Full-resolution image URLs to page through. */
  images: string[];
  /** Active index, or null when the lightbox is closed. */
  index: number | null;
  /** Called with the new index, or null to close. */
  onIndexChange: (i: number | null) => void;
  alt?: string;
}

/**
 * Fullscreen image gallery (lightbox). Swipe, ‹ › arrows, keyboard (←/→/Esc),
 * a "n / total" counter, and backdrop-to-close. Shared by SpotCard and
 * ReviewSystem so photos open the same way everywhere instead of doing nothing
 * or bubbling a navigation click.
 */
export function ImageLightbox({ images, index, onIndexChange, alt }: ImageLightboxProps) {
  const { t } = useTranslation();
  const swipeStartX = useRef<number | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const open = index !== null && !!images[index];
  const len = images.length;

  const go = (delta: number) => {
    if (index === null || len < 2) return;
    onIndexChange((index + delta + len) % len);
  };
  const close = () => onIndexChange(null);

  useEffect(() => {
    setLoadFailed(false);
  }, [index, images]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, len]);

  if (!open || index === null) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/95 flex flex-col backdrop-blur-md"
      onClick={close}
      onTouchStart={(e) => { swipeStartX.current = e.changedTouches?.[0]?.clientX ?? null; }}
      onTouchEnd={(e) => {
        if (swipeStartX.current == null || len < 2) { swipeStartX.current = null; return; }
        const endX = e.changedTouches?.[0]?.clientX;
        if (typeof endX === 'number') {
          const d = endX - swipeStartX.current;
          if (d <= -40) go(1);
          if (d >= 40) go(-1);
        }
        swipeStartX.current = null;
      }}
    >
      <div className="absolute top-0 right-0 p-4 z-10 flex items-center gap-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}>
        {len > 1 && (
          <span className="text-tiny font-black text-white/80 bg-black/50 rounded-full px-3 py-1">
            {index + 1} / {len}
          </span>
        )}
        <button
          className="w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
          onClick={(e) => { e.stopPropagation(); close(); }}
          aria-label={t('common.close', 'Lukk')}
        >
          <X size={24} />
        </button>
      </div>

      <div className="flex-1 w-full h-full flex items-center justify-center p-2 sm:p-8">
        {loadFailed ? (
          <div
            className="flex flex-col items-center gap-3 text-white/70 select-none"
            onClick={(e) => e.stopPropagation()}
          >
            <ImageOff size={40} strokeWidth={1.5} />
            <p className="text-sm font-medium">
              {t('common.image_load_failed', 'Kunne ikke laste bildet')}
            </p>
            {len > 1 && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); go(1); }}
                className="mt-1 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-tiny font-bold uppercase tracking-wider transition-colors"
              >
                {t('auto.spots.spotcard.aria-label.neste', 'Neste')}
              </button>
            )}
          </div>
        ) : (
          <img
            src={images[index]}
            alt={alt}
            className="max-w-full max-h-full object-contain select-none rounded-lg"
            onClick={(e) => e.stopPropagation()}
            onError={() => setLoadFailed(true)}
          />
        )}
      </div>

      {len > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label={t('auto.spots.spotcard.aria-label.forrige', 'Forrige')}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/55 border border-white/20 text-white text-2xl leading-none flex items-center justify-center hover:bg-black/75 active:scale-95"
          >
            ‹
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label={t('auto.spots.spotcard.aria-label.neste', 'Neste')}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-black/55 border border-white/20 text-white text-2xl leading-none flex items-center justify-center hover:bg-black/75 active:scale-95"
          >
            ›
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
